/**
 * The report filter bar (design §1): one sticky row of date preset, grain, compare and the dimension pickers, then the
 * active filters as chips with Clear. Every control writes the URL, so a view is shared by copying the address.
 */
import { Button, Chip, cn, Input, Picker, type PickerOption, Switch } from '@patchbay/ui'
import { X } from 'lucide-react'
import type { FilterOptions } from '../../lib/api/generated'
import { CLEARED_FILTERS, DIMENSIONS, type Dimension, GRAINS, type Grain, isoDay, RANGES, type RangeKey, type ReportsSearch } from './api'

export const selectClass =
  'h-8 min-w-0 rounded-md border border-input bg-card px-2 text-sm text-foreground outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25'

const RANGE_LABEL: Record<RangeKey, string> = {
  '7d': 'Last 7 days',
  '4w': 'Last 4 weeks',
  '12w': 'Last 12 weeks',
  '6m': 'Last 6 months',
  '12m': 'Last 12 months',
  custom: 'Custom dates',
}

/** What the API picks when the URL names no window (common.DEFAULT_DAYS). */
const DEFAULT_RANGE_LABEL: Record<Grain, string> = { day: 'Last 14 days', week: 'Last 12 weeks', month: 'Last 12 months' }

const GRAIN_LABEL: Record<Grain, string> = { day: 'Day', week: 'Week', month: 'Month' }

export const DIMENSION_LABEL: Record<Dimension, string> = {
  owners: 'Owner',
  customers: 'Customer',
  priorities: 'Priority',
  channels: 'Channel',
  categories: 'Category',
  contract_kinds: 'Contract',
}

export function dimensionOptions(options: FilterOptions | undefined, dim: Dimension): PickerOption[] | undefined {
  if (!options) return undefined
  if (dim === 'owners') return (options.owners ?? []).map((u) => ({ value: u.id, label: u.name }))
  return (options[dim] ?? []).map((o) => ({ value: o.key, label: o.label }))
}

export function rangeLabel(search: ReportsSearch): string {
  if (!search.range) return DEFAULT_RANGE_LABEL[search.grain ?? 'week']
  if (search.range === 'custom') return `${search.from ?? 'start'} to ${search.to ?? 'today'}`
  return RANGE_LABEL[search.range]
}

export function FilterBar({
  search,
  onChange,
  options,
  hide,
}: {
  search: ReportsSearch
  onChange: (patch: Partial<ReportsSearch>) => void
  /** Catalogue filter options; undefined when the catalogue did not load (the pickers say so). */
  options: FilterOptions | undefined
  /** Dimensions this page does not offer (a tech's own Team numbers hide Owner). */
  hide?: readonly Dimension[]
}) {
  const grain: Grain = search.grain ?? 'week'
  const dims = hide?.length ? DIMENSIONS.filter((d) => !hide.includes(d)) : DIMENSIONS
  const active = dims.flatMap((dim) => (search[dim] ?? []).map((value) => ({ dim, value })))
  const labelOf = (dim: Dimension, value: string) => dimensionOptions(options, dim)?.find((o) => o.value === value)?.label ?? value
  const anyFilter = active.length > 0 || Boolean(search.range || search.grain || search.compare)
  const today = isoDay(new Date())

  return (
    <search
      aria-label="Report filters"
      className="sticky top-0 z-20 space-y-2 border-b border-border bg-background/95 px-4 py-2.5 backdrop-blur sm:px-5"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: Picker renders the nested combobox input. */}
        <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <span className="sr-only">Date range</span>
          <Picker
            aria-label="Date range"
            className={cn(selectClass, 'w-40')}
            value={search.range ?? ''}
            onChange={(e) => {
              const range = (e.currentTarget.value || undefined) as RangeKey | undefined
              onChange(
                range === 'custom' ? { range, from: search.from, to: search.to ?? today } : { range, from: undefined, to: undefined },
              )
            }}
          >
            <option value="">{DEFAULT_RANGE_LABEL[grain]}</option>
            {RANGES.filter((r) => r === search.range || RANGE_LABEL[r] !== DEFAULT_RANGE_LABEL[grain]).map((r) => (
              <option key={r} value={r}>
                {RANGE_LABEL[r]}
              </option>
            ))}
          </Picker>
        </label>
        {search.range === 'custom' ? (
          <span className="flex items-center gap-1.5">
            <Input
              type="date"
              aria-label="From"
              className="h-8 w-38"
              max={search.to ?? today}
              value={search.from ?? ''}
              onChange={(e) => onChange({ from: e.currentTarget.value || undefined })}
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              aria-label="To"
              className="h-8 w-38"
              max={today}
              value={search.to ?? ''}
              onChange={(e) => onChange({ to: e.currentTarget.value || undefined })}
            />
          </span>
        ) : null}
        <fieldset className="flex items-center rounded-md border border-input bg-card p-0.5" aria-label="Grain">
          <legend className="sr-only">Grain</legend>
          {GRAINS.map((g) => (
            <button
              key={g}
              type="button"
              aria-pressed={grain === g}
              className={cn(
                'h-7 cursor-pointer rounded px-2.5 text-xs font-medium text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                grain === g ? 'bg-primary-soft text-primary' : 'hover:bg-muted',
              )}
              onClick={() => onChange({ grain: g === 'week' ? undefined : g })}
            >
              {GRAIN_LABEL[g]}
            </button>
          ))}
        </fieldset>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Switch
            aria-label="Compare with the prior period"
            checked={search.compare !== 'none'}
            onCheckedChange={(on) => onChange({ compare: on ? undefined : 'none' })}
          />
          vs prior
        </label>
        {dims.map((dim) => {
          const opts = dimensionOptions(options, dim)
          const value = search[dim] ?? []
          // A value from a shared link stays selectable even when the options list lacks it.
          const merged = [...(opts ?? []), ...value.filter((v) => !opts?.some((o) => o.value === v)).map((v) => ({ value: v, label: v }))]
          return (
            <div key={dim} className="w-40">
              <Picker
                multiple
                aria-label={DIMENSION_LABEL[dim]}
                placeholder={opts ? DIMENSION_LABEL[dim] : `${DIMENSION_LABEL[dim]} (unavailable)`}
                title={opts ? undefined : 'Options unavailable (the reports catalogue did not load)'}
                className="min-h-8"
                options={merged}
                value={value}
                onValueChange={(next) => onChange({ [dim]: next.length ? next : undefined })}
              />
            </div>
          )
        })}
      </div>
      {anyFilter ? (
        <ul className="flex flex-wrap items-center gap-1.5" aria-label="Active filters">
          {active.map(({ dim, value }) => (
            <li key={`${dim}:${value}`}>
              <Chip tone="primary" size="lg">
                <span className="text-primary/70">{DIMENSION_LABEL[dim]}:</span> {labelOf(dim, value)}
                <button
                  type="button"
                  aria-label={`Remove ${DIMENSION_LABEL[dim]} ${labelOf(dim, value)}`}
                  className="cursor-pointer rounded hover:bg-primary/10"
                  onClick={() => {
                    const next = (search[dim] ?? []).filter((v) => v !== value)
                    onChange({ [dim]: next.length ? next : undefined })
                  }}
                >
                  <X className="size-3" />
                </button>
              </Chip>
            </li>
          ))}
          <li>
            <Button variant="link" size="sm" onClick={() => onChange(CLEARED_FILTERS)}>
              Clear filters
            </Button>
          </li>
        </ul>
      ) : null}
    </search>
  )
}
